import React, { useEffect, useRef, useState, useLayoutEffect, useCallback } from 'react';
import { observer } from "mobx-react";
import HomePageChatWindow from '../../components/DocumentCanvas/HomePageChatWindow';
import { ChatInput } from '../v2/Input';
import { ChatBoxContainer, ChatMessagesWrapper, ChatInputWrapper } from './ChatBox.styled';
import dayjs from 'dayjs'
import { useStore, useWebSocketScope } from '../../hooks';
import { ChatCommunicationService } from '../../services/ChatCommunicationService';
import { WebSocketScope } from '../../utils/enums';

const ChatBox = observer(({ customerSelected, agentId, agentDomain, inputWidth, showNewChatButton, metadata = {}, initialMessage = null, handleMetadata, agentData = {}, onMessagesUpdate = null }) => {
    const { authStore, conversationsStore } = useStore()
    const [agentIsTyping, setAgentIsTyping] = useState(false);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [selectedAgent, setSelectedAgent] = useState("");
    const [selectedAgentData, setSelectedAgentData] = useState({});
    const [agent_params, setAgent_params] = useState({
        message: "",
        campaign_id: "",
        campaign_version: "",
        agent: ""
    });
    const [attachment, setAttachment] = useState("");
    const [isSendMessageClicked, setIsSendMessageClicked] = useState(false);
    const [conversationID, setConversationID] = useState("");
    const [showStopButton, setShowStopButton] = useState(false);
    const [webSearchEnabled, setWebSearchEnabled] = useState(false);
    const [regeneratingMessageIndex, setRegeneratingMessageIndex] = useState(null);
    const bottomRef = useRef(null);
    const textAreaRef = useRef();
    const streamingMessagesRef = useRef(new Map()); // Track streaming messages by messageId
    const streamingChunksRef = useRef(new Map()); // Track chunks by messageId -> Map<chunkIndex, chunkContent>
    customerSelected && conversationsStore.setCustomerId(customerSelected);

    // Initialize chat communication service - ALWAYS use HTTP
    const chatService = React.useMemo(() => {
        return new ChatCommunicationService('http');
    }, []);

    // WebSocket subscription for CONVERSATION scope with DELTA event
    // Relies on backend sending isLastChunk: true when streaming is complete
    useWebSocketScope(
        WebSocketScope.CONVERSATION,
        useCallback((message) => {
            const payload = message.payload || {};
            const { chunkContent, chunkIndex, isLastChunk, conversationUuid, messageId } = payload;
            
            // Strict filter by conversation - only accept messages for our conversation
            // If we have a conversation ID, only accept messages for that conversation
            if (conversationID) {
                if (!conversationUuid || conversationUuid !== conversationID) {
                    return;
                }
            } else {
                // If we don't have a conversation yet, don't accept any WebSocket messages
                // (we'll get the conversation ID from the send message response)
                return;
            }
            
            // Handle empty content with isLastChunk (cleanup signal)
            if (!chunkContent) {
                if (isLastChunk && messageId) {
                    streamingMessagesRef.current.delete(messageId);
                    streamingChunksRef.current.delete(messageId);
                    setAgentIsTyping(false);
                    setIsSendMessageClicked(false);
                }
                return;
            }
            
            // Initialize tracking for new message
            if (!streamingChunksRef.current.has(messageId)) {
                streamingChunksRef.current.set(messageId, new Map());
                streamingMessagesRef.current.set(messageId, '');
                setAgentIsTyping(false);
            }
            
            // Store chunk by index
            const chunksMap = streamingChunksRef.current.get(messageId);
            const effectiveIndex = typeof chunkIndex === 'number' ? chunkIndex : chunksMap.size;
            chunksMap.set(effectiveIndex, chunkContent);
            
            // Reconstruct content in order (sorted by chunk index)
            const sortedIndices = Array.from(chunksMap.keys()).sort((a, b) => a - b);
            const reconstructedContent = sortedIndices.map(idx => chunksMap.get(idx)).join('');
            streamingMessagesRef.current.set(messageId, reconstructedContent);
            
            // Update message in state
            setMessages(prevMessages => {
                const newMessages = [...prevMessages];
                const existingIndex = newMessages.findIndex(m => m.messageId === messageId);
                
                const messageData = {
                    message: reconstructedContent,
                    type: 'received',
                    time: dayjs().format('HH:mm'),
                    messageId: messageId,
                    conversationUuid: conversationUuid,
                    isStreaming: !isLastChunk,
                };
                
                if (existingIndex >= 0) {
                    newMessages[existingIndex] = { ...newMessages[existingIndex], ...messageData };
                } else {
                    newMessages.push(messageData);
                }
                
                return newMessages;
            });
            
            // Cleanup when stream is complete
            if (isLastChunk) {
                streamingMessagesRef.current.delete(messageId);
                streamingChunksRef.current.delete(messageId);
                setAgentIsTyping(false);
                setIsSendMessageClicked(false);
            }
        }, [conversationID]),
        'DELTA'
    );

    // Notify parent component when messages change
    useEffect(() => {
        if (onMessagesUpdate) {
            onMessagesUpdate(messages);
        }
    }, [messages, onMessagesUpdate]);

    useEffect(() => {
        if (agentId) {
            setSelectedAgent({ id: agentId, domain: agentDomain });
            setAgent_params((prevParams) => ({
                ...prevParams,
                agent: agentId
            }));
        }
    }, [agentId])

    useEffect(() => {
        if (Object.keys(agentData).length) {
            setSelectedAgentData(agentData)
        }
    }, [agentData])

    useEffect(() => {
        if (agent_params.agent && initialMessage) {
            handleSendMessage(initialMessage, { hideMessage: true })
        }
    }, [agent_params.agent, initialMessage])

    useLayoutEffect(() => {
        const textAreaWrapper = textAreaRef.current;
        if (textAreaWrapper) {
            const textArea = textAreaWrapper.getElementsByTagName('textarea')[0];
            if (textArea) {
                textArea.style.height = "0px";
                const scrollHeight = textArea.scrollHeight;
                textArea.style.height = scrollHeight + "px";
            }
        }
    }, [newMessage]);

    const handleCleanThread = () => {
        setMessages([]);
        setAgentIsTyping(false);
        setConversationID("");
        setIsSendMessageClicked(false);
        setAttachment("");
        setShowStopButton(false);
        // Clear streaming refs
        streamingMessagesRef.current.clear();
        streamingChunksRef.current.clear();
    }

    const setErrorInChat = () => {
        setAgentIsTyping(false);
        setMessages([
            ...messages,
            {
                message:
                    "Oops! Something went wrong. Please try again.",
                type: "received",
                time: dayjs().format('HH:mm'),
            },
        ]);
        setShowStopButton(false);
    }

    const handleSendMessage = async (sentMessage, options = {}) => {
        // Handle both old hideMessage parameter and new options parameter for backwards compatibility
        let hideMessage = false;
        let useWebSearch = webSearchEnabled;
        
        if (typeof options === 'boolean') {
            // Old API: handleSendMessage(message, hideMessage)
            hideMessage = options;
        } else {
            // New API: handleSendMessage(message, { useWebSearch, hideMessage })
            hideMessage = options.hideMessage || false;
            useWebSearch = options.useWebSearch !== undefined ? options.useWebSearch : webSearchEnabled;
        }

        const messageToSend = sentMessage || newMessage;
        if (!messageToSend || messageToSend.trim() === "") return;

        setIsSendMessageClicked(true);
        setNewMessage("");

        // Add user message to chat (unless hidden)
        if (!hideMessage) {
            setMessages(prev => [...prev, {
                message: messageToSend,
                type: "sent",
                time: dayjs().format('HH:mm')
            }]);
        }

        try {
            setAgentIsTyping(true);
            
            // Get assistant UUID from agentId or agentData
            const assistant_uuid = agentId || agentData?.uuid || agentData?.id || agent_params.agent;
            
            if (!assistant_uuid) {
                throw new Error('No assistant UUID available');
            }
            
            const params = {
                message_text: messageToSend,
                tenant_id: authStore.currentWorkspaceUuid,
                sender_uuid: authStore.currentWorkspaceUuid || customerSelected,
                assistant_uuid: assistant_uuid,
                conversation_uuid: conversationID || "",
                channel: "",
                metadata: { useWebSearch },
                attachment: attachment || undefined,
                user_uuid: authStore.userId,
            };

            const result = await chatService.sendMessage(params);
            
            if (result.success) {
                // Clear attachment after successful message send
                setAttachment("");
                
                // Update conversation ID from response
                const newConversationId = result.data?.body?.conversation_uuid || 
                                          result.data?.conversationUuid || 
                                          result.data?.conversation_id;
                
                if (newConversationId) {
                    setConversationID(newConversationId);
                }
                
                // DON'T add assistant response here - wait for chunks from WebSocket
                // DON'T reset isSendMessageClicked here - will be reset when last chunk arrives
            } else {
                throw new Error(result.error || "Failed to send message");
            }
        } catch (err) {
            console.error("Error sending message:", err);
            setAgentIsTyping(false);
            setIsSendMessageClicked(false);
            setErrorInChat();
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        bottomRef.current.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "start"
        });
    };

    const handleStopClick = () => {
        // Stop any ongoing streaming
        streamingMessagesRef.current.clear();
        streamingChunksRef.current.clear();
        setAgentIsTyping(false);
        setShowStopButton(false);
        setIsSendMessageClicked(false);
    };

    // Handler for regenerating a message
    const handleRegenerateMessage = useCallback(async (messageIndex) => {
        if (regeneratingMessageIndex !== null) return; // Already regenerating
        
        // Find the user message that triggered this response
        let userMessageIndex = messageIndex - 1;
        while (userMessageIndex >= 0 && messages[userMessageIndex]?.type !== 'sent') {
            userMessageIndex--;
        }
        
        if (userMessageIndex < 0) return; // No user message found
        
        const userMessage = messages[userMessageIndex];
        setRegeneratingMessageIndex(messageIndex);
        
        // Remove the old response
        setMessages(prev => prev.filter((_, idx) => idx !== messageIndex));
        
        // Resend the user message (hidden)
        await handleSendMessage(userMessage.message, { hideMessage: true });
        
        setRegeneratingMessageIndex(null);
    }, [messages, regeneratingMessageIndex, handleSendMessage]);

    // Handler for adding a message to chat (used by HomeEnhancedMessage)
    const handleAddChatMessage = useCallback((message) => {
        setMessages(prev => [...prev, {
            message: message,
            type: 'sent',
            time: dayjs().format('HH:mm')
        }]);
    }, []);

    return (
        <ChatBoxContainer>
            <ChatMessagesWrapper>
                <HomePageChatWindow
                    selectedAgent={selectedAgentData}
                    messages={messages}
                    bottomRef={bottomRef}
                    width={'inherit'}
                    height={'inherit'}
                    agentIsTyping={agentIsTyping}
                    displayButtons={true}
                    onRegenerateMessage={handleRegenerateMessage}
                    regeneratingMessageIndex={regeneratingMessageIndex}
                    onAddChatMessage={handleAddChatMessage}
                    dataTestId="playground-chat-window"
                    showAgentName={false}
                />
            </ChatMessagesWrapper>

            <ChatInputWrapper>
                <ChatInput
                    customerId={authStore.currentWorkspaceUuid}
                    onChange={(e) => setNewMessage(e.target.value)}
                    newMessage={newMessage}
                    handleSendMessage={handleSendMessage}
                    onFileUploaded={setAttachment}
                    textAreaRef={textAreaRef}
                    disableSend={isSendMessageClicked || !newMessage.trim()}
                    onCleanThread={handleCleanThread}
                    showNewChatButton={showNewChatButton}
                    showFileUpload={true}
                    showClearChat={true}
                    stop={showStopButton}
                    onStopClick={handleStopClick}
                    placeholder={agentData.placeholderInput || "Ask anything..."}
                    maxHeight="150px"
                    showWebSearch={agentData?.actions?.includes('web_search')}
                    webSearchEnabled={webSearchEnabled}
                    onWebSearchToggle={setWebSearchEnabled}
                />
            </ChatInputWrapper>
        </ChatBoxContainer>
    );
});

export default ChatBox;
